import { documentsForPlacement } from "../constants/navigationCatalogue";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { usePermissions } from "../context/PermissionContext";
import { useTopNavMenu } from "../context/TopNavMenuContext";
import { DocumentType } from "../types/Types";
import { getDocumentPath } from "../routes/documentPath";
import TopNavLink from "./TopNavLink";
import { modulesForDocumentType } from "../constants/documentModule";

type Entry = {
  key: string;
  path: string;
  label: string;
  state?: Record<string, unknown>;
  divider?: boolean;
};

const docState = (doc: DocumentType) => ({
  _id: doc._id,
  code: doc.code,
  name: doc.name,
  category: doc.category,
  subCategory: doc.subCategory,
  subType: doc.subType,
});

const TopManufacturingMenu: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocumentAccess, hasDocNavigate } = usePermissions();
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const { activeMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose } = useTopNavMenu();
  const open = activeMenu === "manufacturing";

  const manufacturingDocs = useMemo(() => {
    return documentsForPlacement(documentTypes, "manufacturing")
      .sort((a, b) => {
        const numA = Number(a.code);
        const numB = Number(b.code);
        const aNum = !Number.isNaN(numA);
        const bNum = !Number.isNaN(numB);
        if (!aNum && !bNum) return a.code.localeCompare(b.code);
        if (!aNum) return -1;
        if (!bNum) return 1;
        return numA - numB;
      })
      // Module rule mirrors the server (constants/documentModule.ts).
      .filter((doc) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read")));
  }, [documentTypes, hasDocumentAccess]);

  const hasAnyAccess = hasAccess("WORK_ORDERS") || hasAccess("MATERIAL_DOCUMENTS") || manufacturingDocs.length > 0;
  if (!hasAnyAccess) return null;

  const documentEntries: Entry[] = manufacturingDocs
    .filter((doc) => hasDocNavigate(doc.code))
    .map((doc) => ({
      key: doc._id,
      path: getDocumentPath(doc),
      label: `${doc.code} ${doc.name}`.trim(),
      state: docState(doc),
    }));

  const workOrdersEntry: Entry[] = hasAccess("WORK_ORDERS")
    ? [{ key: "work-orders", path: "/work-orders", label: t("navbar.workOrders", "Работни налози") }]
    : [];
  const printEntries: Entry[] = hasAccess("PRINT_CONTROL_PANEL") && hasAccess("WORK_ORDERS")
    ? [{ key: "production-print", path: "/production-control-panel", label: t("sidebar.printControlPanel", "Print"), divider: true }]
    : [];

  const sections = [
    { key: "documents", label: "Документи", entries: [...workOrdersEntry, ...documentEntries, ...printEntries] },
  ].filter((section) => section.entries.length > 0);

  if (sections.length === 0) return null;

  const activeSection = sections.find((section) => section.key === (activeSectionKey || sections[0]?.key)) || sections[0];

  return (
    <div
      className="relative"
      onMouseEnter={() => openMenu("manufacturing")}
      onMouseLeave={closeMenuDelayed}
    >
      <button
        type="button"
        onClick={() => (open ? closeMenu() : openMenu("manufacturing"))}
        className="flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span>{t("sidebar.manufacturing", "Производство")}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && activeSection && (
        <div
          className="absolute left-0 top-full mt-1 flex rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xl"
          onMouseEnter={cancelClose}
        >
          <div className="w-56 p-2">
            {sections.map((section) => {
              const active = section.key === activeSection.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onMouseEnter={() => setActiveSectionKey(section.key)}
                  onClick={() => setActiveSectionKey(section.key)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors ${
                    active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                >
                  <span>{section.label}</span>
                  <ChevronRight size={15} />
                </button>
              );
            })}
          </div>

          <div className="max-h-[70vh] w-80 overflow-y-auto border-l border-gray-200 p-2">
            <div className="space-y-1">
              {activeSection.entries.map((entry) => {
                const active = location.pathname === entry.path || location.pathname.startsWith(`${entry.path}/`);
                return (
                  <React.Fragment key={`${activeSection.key}-${entry.key}`}>
                    {entry.divider && <div className="my-1 border-t border-gray-200" />}
                    <TopNavLink
                      to={entry.path}
                      state={entry.state}
                      label={entry.label}
                      moduleKey={entry.key}
                      isActive={active}
                      onClick={closeMenu}
                      className={`block truncate rounded px-2 py-1.5 text-xs transition-colors ${
                        active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                      }`}
                      style={active ? {
                        color: "var(--color-primary)",
                        backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)",
                      } : undefined}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopManufacturingMenu;
